import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Avatar from "../avatar/Avatar";
import UsuariosService from "../../service/usuario";
import UserDropdown from "./UserDropdown";
import SinoNotificacoes from "./SinoNotificacoes";

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 85px;
  background-color: rgb(241, 240, 240);
  z-index: 950;
  display: flex;
  align-items: center;
  padding-left: 260px;
  justify-content: space-between;
  box-sizing: border-box;
  font-family: "Roboto", sans-serif;

  @media (max-width: 1200px) and (min-width: 768px) {
    /* When the sidebar is collapsed, add extra padding so the header text
       doesn't sit too close to the menu button */
    padding-left: 80px;
  }

  @media (max-width: 767px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const HeaderText = styled.div`
  color: #8e8e93;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: bold;

  @media (max-width: 767px) {
    font-size: 14px;
    &::before {
      content: "TPE - DF";
    }
    font-size: 0; /* Hide original text */
    &::before {
      font-size: 16px; /* Show new text */
      visibility: visible;
    }
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  padding-right: 32px;
  position: relative;

  @media (max-width: 767px) {
    padding-right: 0;
    & > div:nth-child(2) { /* Avatar (o sino é o 1º) */
      display: none;
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 10px;
  position: relative;

  @media (max-width: 767px) {
    margin-left: 0;
  }
`;

const UserNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UserName = styled.span`
  font-family: "Roboto", sans-serif;
  font-size: 18px;
  color: #222;
  font-weight: 400;

  &.desktop-name {
    display: inline;
    @media (max-width: 767px) {
      display: none;
    }
  }

  &.mobile-name {
    display: none !important;
    @media (max-width: 767px) {
      display: inline !important;
    }
  }
`;

const UserRole = styled.span`
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;
  opacity: 0.7;
  margin-top: -2px;

  @media (max-width: 767px) {
    display: none;
  }
`;

const SelectorContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
`;

const OptionsList = styled.div`
  position: absolute;
  right: 32px;
  top: 60px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  z-index: 995;
  min-width: 180px;
`;

const OptionItem = styled.div`
  padding: 8px 12px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #222;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const SelectedPill = styled.span`
  background: #e6f7f7;
  color: #006b6b;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 13px;
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  padding: 0 0 0 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 24px;
  font-family: "Roboto", sans-serif;
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  @media (max-width: 767px) {
    display: inline-flex;
  }
`;

const ArrowIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M7 10l5 5 5-5"
      stroke="#222"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Header = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [usuario, setUsuario] = useState(() => {
    // Carrega dados do usuário do localStorage se disponível
    const usuarioCache = localStorage.getItem("usuarioCache");
    return usuarioCache ? JSON.parse(usuarioCache) : null;
  });

  const fetchUsuario = async (usuarioAtualizado) => {
    try {
      const response = usuarioAtualizado || await UsuariosService.getUsuarioById(localStorage.getItem("idUsuario"));
      setUsuario(response);
      // Salva no localStorage para evitar flickering ao trocar de página
      localStorage.setItem("usuarioCache", JSON.stringify(response));
      return response;
    } catch (error) {
      console.error("Erro ao buscar usuário:", error);
      return null;
    }
  };

  const handleArrowClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    fetchUsuario();
  }, []);

  const nomeCompleto = usuario?.nome || "Usuário";
  const nomeExibicao = usuario?.apelido?.trim() || nomeCompleto;
  const nomeUsuario = nomeExibicao.split(" ").slice(0, 2).join(" ");
  const primeiroNome = nomeExibicao.split(" ")[0];

  const PERFIL_PRIORIDADE = [
    'admin',
    'coordenador-geral',
    'coordenador-regional',
    'coordenador-ponto',
    'secretaria',
    'colaborador',
    'publicador'
  ];

  const getPerfilMaisPrivilegiado = (perfis) => {
    const arr = Array.isArray(perfis) ? perfis : (perfis ? [perfis] : []);
    for (const p of PERFIL_PRIORIDADE) {
      if (arr.includes(p)) return p;
    }
    return arr[0] || null;
  };

  const perfisUsuario = Array.isArray(usuario?.perfil) ? usuario.perfil : (usuario?.perfil ? [usuario.perfil] : []);
  const perfilUsuario = getPerfilMaisPrivilegiado(perfisUsuario);
  const perfilLabel = {
    'admin': 'Administrador',
    'coordenador-geral': 'Coordenador Geral',
    'coordenador-ponto': 'Coordenador de Ponto',
    'coordenador-regional': 'Coordenador Regional',
    'colaborador': 'Colaborador',
    'secretaria': 'Secretária',
    'publicador': 'Publicador'
  }[perfilUsuario];

  const checkboxOptions = [
    { value: 'publicador', label: 'Publicador' },
    { value: 'anciao', label: 'Ancião' },
    { value: 'servo-ministerial', label: 'Servo Ministerial' },
    { value: 'pioneiro-regular', label: 'Pioneiro Regular' },
  ];

  const [privilegesOpen, setPrivilegesOpen] = useState(false);

  const handlePrivilegeSelect = async (value) => {
    try {
      const current = Array.isArray(usuario?.perfil) ? usuario.perfil : (usuario?.perfil ? [usuario.perfil] : []);
      const exists = current.includes(value);
      const next = exists ? current.filter((p) => p !== value) : Array.from(new Set([...(current || []), value]));
      const userId = localStorage.getItem('idUsuario');
      await UsuariosService.updateUsuario(userId, { perfil: next });
      await fetchUsuario();
      setPrivilegesOpen(false);
    } catch (error) {
      console.error('Erro ao atualizar privilégios:', error);
    }
  };

  return (
    <HeaderContainer>
      <LeftGroup>
        <MenuButton
          type="button"
          aria-label="Abrir menu de navegação"
          onClick={() => {
            const event = new CustomEvent("toggle-main-menu");
            window.dispatchEvent(event);
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor" />
          </svg>
        </MenuButton>
        <HeaderText>Testemunho Público Especial - DF</HeaderText>
      </LeftGroup>
      <RightContainer>
        <SinoNotificacoes />
        <Avatar name={nomeUsuario} />
        <UserInfo>
          <UserNameRow>
            <UserName className="desktop-name">{nomeUsuario}</UserName>
            <UserName className="mobile-name" style={{ display: 'none' }}>{primeiroNome}</UserName>
            <ArrowButton
              onClick={handleArrowClick}
              aria-label="Abrir menu do usuário"
              data-dropdown-trigger
            >
              <ArrowIcon />
            </ArrowButton>
          </UserNameRow>
          {perfilLabel && <UserRole>{perfilLabel}</UserRole>}
          <UserDropdown 
            isOpen={dropdownOpen} 
            onClose={() => setDropdownOpen(false)} 
            usuario={usuario} 
            onUpdate={fetchUsuario} 
          />
        </UserInfo>
      </RightContainer>
    </HeaderContainer>
  );
};

export default Header;
